import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material'
import {
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  Logout as LogoutIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import PatientSelector from './PatientSelector'
import { useAuth } from '../contexts/AuthContext'

const drawerWidth = 200

// Navigation structure with collapsible sections.
// requiredRoles: array of roles that can see this section/item (empty = all roles)
// cluster: visual grouping — sections with the same cluster value get a shared
//   tinted background. 'clinical' = running active interventions and patients;
//   'design' = authoring intervention programs and content.
const navigationSections = [
  {
    title: 'Overview',
    items: [
      { text: 'Dashboard', path: '/dashboard' },
    ],
  },
  {
    title: 'Intervention Management',
    cluster: 'clinical',
    items: [
      { text: 'Intervention Dashboard', path: '/intervention-dashboard' },
    ],
  },
  {
    title: 'Patient Management',
    cluster: 'clinical',
    items: [
      // Items above the divider operate on the currently-selected patient.
      { text: 'Patient Dashboard', path: '/patient-dashboard' },
      { text: 'Health Data', path: '/journey-milestones' },
      { text: 'Assessments', path: '/assessment-results' },
      { text: 'Therapeutic Models', path: '/therapeutic-models' },
      // Items below the divider operate across all patients.
      { divider: true },
      { text: 'All Patients', path: '/patient-assignments' },
    ],
  },
  {
    title: 'Intervention Design',
    cluster: 'design',
    requiredRoles: ['system_admin', 'administrator', 'clinical_designer'],
    items: [
      { text: 'Intervention Programs', path: '/intervention-configs' },
      { text: 'Session Preview', path: '/session-preview' },
    ],
  },
  {
    title: 'Content Library',
    cluster: 'design',
    items: [
      { text: 'Pathways', path: '/tracks' },
      { text: 'Activities', path: '/activities' },
      { text: 'Domains', path: '/domains' },
      { text: 'Education Modules', path: '/education-modules' },
      { text: 'Assessment Config', path: '/assessment-config' },
      { text: 'Risk Codes', path: '/risk-codes' },
      // Items below the divider manage pathway content data.
      { divider: true },
      { text: 'Content Groups', path: '/content-groups' },
      { text: 'Resources', path: '/resources' },
      { text: 'Information Sources', path: '/information-sources' },
      { text: 'QOL Concerns', path: '/qol-concerns' },
    ],
  },
  {
    title: 'Analytics',
    items: [
      { text: 'Analytics', path: '/analytics' },
    ],
  },
]

// Subtle tinted backgrounds for each cluster — low-alpha so they sit behind
// the menu text without competing for attention.
const clusterStyles = {
  clinical: { bgcolor: 'rgba(15, 118, 110, 0.05)' },  // teal tint (primary family)
  design:   { bgcolor: 'rgba(113, 113, 122, 0.06)' }, // warm gray tint
}

const systemSection = {
  title: 'System',
  requiredRoles: ['system_admin', 'administrator'],
  items: [
    { text: 'Agent Prompts', path: '/agent-prompts', requiredRoles: ['system_admin'] },
    { text: 'User Management', path: '/system/users' },
    { text: 'Audit Log', path: '/system/audit' },
  ],
}

// Platform Config section - only visible to system_admin
const platformConfigSection = {
  title: 'Platform Config',
  requiredRoles: ['system_admin'],
  items: [
    { text: 'Base Content', path: '/platform/base-content' },
  ],
}

// Paths that display the patient selector in the AppBar
const patientViewPaths = ['/patient-dashboard', '/journey-milestones', '/assessment-results', '/therapeutic-models']

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [openSections, setOpenSections] = useState({})
  const [userMenuAnchor, setUserMenuAnchor] = useState(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { adminUser, logout, hasRole } = useAuth()

  // Filter sections based on user role
  const canViewSection = useCallback((section) => {
    if (!section.requiredRoles || section.requiredRoles.length === 0) return true
    return hasRole(section.requiredRoles)
  }, [hasRole])

  const visibleNavigationSections = useMemo(
    () => navigationSections.filter(canViewSection),
    [canViewSection]
  )

  // Initialize open sections based on current path
  useEffect(() => {
    const initialOpenSections = {}
    visibleNavigationSections.forEach((section) => {
      const isCurrentSection = section.items.some(
        (item) => location.pathname === item.path || location.pathname.startsWith(item.path + '/')
      )
      if (isCurrentSection) {
        initialOpenSections[section.title] = true
      }
    })
    // Check system section
    if (canViewSection(systemSection) && systemSection.items.some((item) => location.pathname.startsWith(item.path))) {
      initialOpenSections[systemSection.title] = true
    }
    // Check platform config section
    if (canViewSection(platformConfigSection) && platformConfigSection.items.some((item) => location.pathname.startsWith(item.path))) {
      initialOpenSections[platformConfigSection.title] = true
    }
    setOpenSections((prev) => ({ ...prev, ...initialOpenSections }))
  }, [location.pathname, visibleNavigationSections, canViewSection])

  // Check if current page is a Patient View page
  const isPatientViewPage = () => {
    return patientViewPaths.includes(location.pathname)
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleSectionToggle = (sectionTitle) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle],
    }))
  }

  const handleNavigation = (path) => {
    navigate(path)
    setMobileOpen(false)
  }

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget)
  }

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null)
  }

  const handleLogout = async () => {
    handleUserMenuClose()
    await logout()
    navigate('/login')
  }

  // Check if a path is currently active (exact match or starts with for nested routes)
  const isPathActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  // Get current page title for AppBar
  const getCurrentPageTitle = () => {
    for (const section of navigationSections) {
      const item = section.items.find((item) => isPathActive(item.path))
      if (item) return item.text
    }
    const systemItem = systemSection.items.find((item) => isPathActive(item.path))
    if (systemItem) return systemItem.text
    const platformItem = platformConfigSection.items.find((item) => isPathActive(item.path))
    if (platformItem) return platformItem.text
    return 'ASTA Management Portal'
  }

  const renderNavigationSection = (section) => (
    <React.Fragment key={section.title}>
      <ListItem disablePadding sx={{ mt: 1.5 }}>
        <ListItemButton
          onClick={() => handleSectionToggle(section.title)}
          sx={{
            py: 0.5,
            px: 1.5,
            '&:hover': { bgcolor: 'transparent' },
          }}
        >
          <ListItemText
            primary={section.title}
            primaryTypographyProps={{
              variant: 'caption',
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.6875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          />
          {openSections[section.title] ? (
            <ExpandLess sx={{ fontSize: 16, color: 'text.secondary' }} />
          ) : (
            <ExpandMore sx={{ fontSize: 16, color: 'text.secondary' }} />
          )}
        </ListItemButton>
      </ListItem>
      <Collapse in={openSections[section.title]} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {section.items
            .filter((item) => !item.requiredRoles || hasRole(item.requiredRoles))
            .map((item, i) => {
            if (item.divider) {
              return (
                <Divider
                  key={`divider-${i}`}
                  sx={{ my: 0.75, mx: 2, borderColor: 'rgba(0, 0, 0, 0.08)' }}
                />
              )
            }
            const active = isPathActive(item.path)
            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={active}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    py: 0.5,
                    px: 1.5,
                    ml: 1,
                    // Always-present transparent border prevents pixel shift when selected
                    borderLeft: '2px solid',
                    borderColor: active ? 'primary.main' : 'transparent',
                    borderRadius: 0,
                    bgcolor: active ? 'rgba(15, 118, 110, 0.1)' : 'transparent',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(15, 118, 110, 0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(15, 118, 110, 0.14)',
                      },
                    },
                  }}
                >
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.8125rem',
                      // Keep weight constant so active text doesn't reflow
                      fontWeight: 400,
                      color: active ? 'primary.main' : 'text.primary',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Collapse>
    </React.Fragment>
  )

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ minHeight: '48px !important', px: 2 }}>
        <Typography
          variant="subtitle1"
          noWrap
          component="div"
          sx={{ fontWeight: 600, color: 'primary.main', letterSpacing: '0.05em' }}
        >
          ASTA
        </Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 0.5 }}>
        {/* Group consecutive sections by cluster so we can apply a shared
            tinted background to each visual grouping. */}
        {(() => {
          const groups = []
          let current = null
          for (const section of visibleNavigationSections) {
            const cluster = section.cluster || null
            if (!current || current.cluster !== cluster) {
              current = { cluster, sections: [] }
              groups.push(current)
            }
            current.sections.push(section)
          }
          return groups.map((group, idx) => {
            const tint = group.cluster ? clusterStyles[group.cluster] : null
            return (
              <Box
                key={group.cluster || `ungrouped-${idx}`}
                sx={{
                  ...(tint || {}),
                  ...(tint ? { py: 0.5, mb: 0.5 } : {}),
                }}
              >
                <List sx={{ pt: 0 }}>
                  {group.sections.map((section) => renderNavigationSection(section))}
                </List>
              </Box>
            )
          })
        })()}

        {/* System section - role-restricted to administrators and system_admin */}
        {canViewSection(systemSection) && (
          <>
            <Divider sx={{ my: 1 }} />
            <List>
              {renderNavigationSection(systemSection)}
            </List>
          </>
        )}

        {/* Platform Config section - system_admin only */}
        {canViewSection(platformConfigSection) && (
          <>
            <Divider sx={{ my: 1 }} />
            <List>
              {renderNavigationSection(platformConfigSection)}
            </List>
          </>
        )}
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
      >
        <Toolbar sx={{ minHeight: '48px !important' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {getCurrentPageTitle()}
          </Typography>
          {isPatientViewPage() && (
            <Box sx={{ display: { xs: 'none', sm: 'block' }, mr: 2 }}>
              <PatientSelector />
            </Box>
          )}
          {adminUser && (
            <>
              <IconButton
                color="inherit"
                onClick={handleUserMenuOpen}
                size="small"
                sx={{ ml: 1 }}
              >
                <PersonIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem disabled sx={{ opacity: 1 }}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {adminUser.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {adminUser.email}
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2.5,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          bgcolor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: '48px !important' }} />
        <Box
          sx={{
            width: '100%',
            maxWidth: 1400,
            minWidth: 0,
            // Prevent horizontal overflow from expanding container
            '& > *': {
              maxWidth: '100%',
              boxSizing: 'border-box',
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export default Layout
